'use client'
import {UiErrorText} from "@/components/i18n/ui-error-text"
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"

import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import { Loader2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  useClearShopifyTopupBlockMutation,
  useResolveShopifyTopupAttemptMutation,
} from '@/hooks/mutations/shopify-topup-incidents'
import { useShopifyTopupIncidentQuery } from '@/hooks/queries/shopify-topup-incidents'
import { formatCentsAsUsd } from '@/lib/billing/utils/format'

import type { ShopifyTopupAttemptSummary } from '@/lib/billing/topup/shopify-topup-incident'
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




interface ShopifyTopupIncidentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tenantId: string
  tenantName: string
  canResolve: boolean
  canClearBlock: boolean
  onChanged: () => void
}

/**
 * Resolves what the settlement sweep could not. Both actions here move real money or lift
 * a billing hold, so each attempt is closed one at a time rather than in bulk.
 */
export function ShopifyTopupIncidentDialog({
  open, onOpenChange, tenantId, tenantName, canResolve, canClearBlock, onChanged,
}: ShopifyTopupIncidentDialogProps) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const tUi = useTranslations("Billing.BillingShopifyTopupIncidentDialog")

  const { data, isLoading, isError, error } = useShopifyTopupIncidentQuery(tenantId, open)
  const resolve = useResolveShopifyTopupAttemptMutation(tenantId, onChanged)
  const clearBlock = useClearShopifyTopupBlockMutation(tenantId, onChanged)
  const busy = resolve.isPending || clearBlock.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[860px]">
        <DialogHeader>
          <DialogTitle><span><span key={tUi("shopifyTopUps")}>{tUi("shopifyTopUps")}</span></span> {tenantName}</DialogTitle>
          <DialogDescription>
            <span key={tUi("lookEachAttemptUpByItsIdempotencyKey")}>{tUi("lookEachAttemptUpByItsIdempotencyKey")}</span></DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <p key={error instanceof Error ? error.message : tUi("failedToLoadTheIncident")} className="py-6 text-sm text-destructive">
            <UiErrorText message={error instanceof Error ? error.message : undefined} fallback={tUi("failedToLoadTheIncident")} />
          </p>
        )}

        {data && (
          <div className="space-y-4">
            {data.blockedAt ? (
              <Alert variant="destructive">
                <AlertDescription className="space-y-2">
                  <div>
                    <span><span key={tUi("topUpsPaused")}>{tUi("topUpsPaused")}</span></span> {new Date(data.blockedAt).toLocaleString(uiFormattingLocale)} <span>—</span> {data.blockedReason}
                  </div>
                  {canClearBlock && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy || data.escalatedCount > 0}
                      onClick={() => clearBlock.mutate()}
                    >
                      {clearBlock.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                      <span><span key={tUi("liftTheHold")}>{tUi("liftTheHold")}</span></span>
                    </Button>
                  )}
                  {data.escalatedCount > 0 && (
                    <div className="text-xs">
                      <span><span key={tUi("closeThe")}>{tUi("closeThe")}</span></span> {data.escalatedCount} <span><span key={tUi("escalatedAttemptSFirstReconciliationStaysBlindTo")}>{tUi("escalatedAttemptSFirstReconciliationStaysBlindTo")}</span></span>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            ) : (
              <p className="text-sm text-muted-foreground"><span key={tUi("topUpsAreNotBlockedForThisTenant")}>{tUi("topUpsAreNotBlockedForThisTenant")}</span></p>
            )}

            {data.unsettled.length === 0 ? (
              <p className="text-sm text-muted-foreground"><span key={tUi("noUnsettledAttempts")}>{tUi("noUnsettledAttempts")}</span></p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead><span key={tUi("meter")}>{tUi("meter")}</span></TableHead>
                    <TableHead><span key={tUi("charge")}>{tUi("charge")}</span></TableHead>
                    <TableHead><span key={tUi("creditOwed")}>{tUi("creditOwed")}</span></TableHead>
                    <TableHead><span key={tUi("state")}>{tUi("state")}</span></TableHead>
                    <TableHead><span key={tUi("idempotencyKey")}>{tUi("idempotencyKey")}</span></TableHead>
                    {canResolve && <TableHead className="w-[190px]"><span key={tUi("resolve")}>{tUi("resolve")}</span></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.unsettled.map((attempt) => (
                    <AttemptRow
                      key={attempt.id}
                      attempt={attempt}
                      canResolve={canResolve}
                      busy={busy}
                      onResolve={(resolution) => resolve.mutate({ attemptId: attempt.id, resolution })}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function AttemptRow({
  attempt, canResolve, busy, onResolve,
}: {
  attempt: ShopifyTopupAttemptSummary
  canResolve: boolean
  busy: boolean
  onResolve: (resolution: 'credited' | 'failed') => void
}) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const { formatCredits } = useCreditFormatters()


  const tUi = useTranslations("Billing.BillingShopifyTopupIncidentDialog")

  return (
    <TableRow>
      <TableCell className="font-medium">{attempt.meterHandle}</TableCell>
      <TableCell>{formatCentsAsUsd(attempt.priceCents)}</TableCell>
      <TableCell>{formatCredits(attempt.creditMicrousd)}</TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <Badge variant={attempt.escalatedAt ? 'destructive' : 'outline'}>
            <span key={attempt.escalatedAt ? tUi("escalated") : attempt.status}>{attempt.escalatedAt ? tUi("escalated") : attempt.status}</span>
          </Badge>
          <span className="text-xs text-muted-foreground"><TranslationText>{tUi.rich("value0AttemptSValue1", { value0: String(attempt.attemptCount), value1: String(new Date(attempt.createdAt).toLocaleString(uiFormattingLocale)) })}</TranslationText></span>
          {attempt.error && (
            <span className="text-xs text-muted-foreground">{attempt.error}</span>
          )}
        </div>
      </TableCell>
      <TableCell className="font-mono text-xs">{attempt.idempotencyKey}</TableCell>
      {canResolve && (
        <TableCell>
          <div className="flex gap-1">
            {/* Shopify billed it — pay the merchant what they bought. */}
            <Button size="sm" disabled={busy} onClick={() => onResolve('credited')}>
              <span key={tUi("credit")}>{tUi("credit")}</span></Button>
            {/* Shopify never billed it — nothing is owed. */}
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onResolve('failed')}>
              <span key={tUi("writeOff")}>{tUi("writeOff")}</span></Button>
          </div>
        </TableCell>
      )}
    </TableRow>
  )
}
